/**
 * Iter 84.2 / Phase E5 Sprint 2 — Statutory Bonus Console (D35-A/B/C/D).
 *
 *  Permission gates:
 *    bonus:generate — initiate batches
 *    bonus:approve  — 4-eye approval (cannot self-approve)
 *    bonus:export   — download Form C CSV
 *    compliance:seal— terminal seal (super_admin)
 *
 *  Snapshot architecture (D35-D): per-employee rows are captured at
 *  creation time and NEVER recomputed from live employee data. Form C
 *  always reflects the snapshot — even if employee structures change
 *  after approval.
 */
import { useCallback, useEffect, useState } from "react";
import {
    Sparkle, CheckCircle, DownloadSimple, ShieldCheck,
    ArrowsClockwise, Lock,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
    DialogDescription, DialogFooter,
} from "../../components/ui/dialog";

const fmtMoney = (v) =>
    v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const STATUS_COLORS = {
    Draft: "bg-slate-100 text-slate-700",
    Approved: "bg-emerald-100 text-emerald-700",
    Sealed: "bg-blue-100 text-blue-700",
};

export default function StatutoryBonusConsole() {
    const { can } = useAuth();
    const canGenerate = can("bonus:generate");
    const canApprove = can("bonus:approve");
    const canExport = can("bonus:export");
    const canSeal = can("compliance:seal");

    const [batches, setBatches] = useState([]);
    const [eligibleRuns, setEligibleRuns] = useState([]);
    const [loading, setLoading] = useState(false);

    // Builder state
    const [fy, setFy] = useState("2025-2026");
    const [declaredPct, setDeclaredPct] = useState("8.33");
    const [allocSurplus, setAllocSurplus] = useState("0");
    const [selectedRuns, setSelectedRuns] = useState([]);
    const [preview, setPreview] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);

    // Detail dialog
    const [detail, setDetail] = useState(null);
    const [detailEmployees, setDetailEmployees] = useState([]);
    const [detailOpen, setDetailOpen] = useState(false);

    const reload = useCallback(async () => {
        try {
            const [b, r] = await Promise.all([
                api.get("/compliance/bonus/batches"),
                api.get("/compliance/eligible-payroll-runs?limit=100"),
            ]);
            setBatches(b.data.items || []);
            setEligibleRuns(r.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load bonus data"));
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    const toggleRun = (id) =>
        setSelectedRuns((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );

    const buildBody = () => ({
        financial_year: fy,
        declared_percent: parseFloat(declaredPct),
        wage_ceiling: 21000,
        calculation_ceiling: 7000,
        allocable_surplus: parseFloat(allocSurplus || "0"),
        payroll_run_ids: selectedRuns,
    });

    const handlePreview = async () => {
        if (selectedRuns.length === 0) {
            toast.error("Select at least 1 payroll run for the FY");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/compliance/bonus/preview", buildBody());
            setPreview(data);
            setPreviewOpen(true);
        } catch (err) {
            toast.error(formatApiError(err, "Preview failed"));
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async () => {
        if (!canGenerate) {
            toast.error("Missing bonus:generate permission");
            return;
        }
        if (selectedRuns.length === 0) {
            toast.error("Select at least 1 payroll run for the FY");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/compliance/bonus/batch/create", {
                ...buildBody(),
                note: `FY ${fy} · ${declaredPct}% declared`,
            });
            toast.success(`Bonus batch created · ${data.n_eligible} eligible · ${fmtMoney(data.total_bonus)}`);
            setPreviewOpen(false);
            setSelectedRuns([]);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Batch creation failed"));
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (batchId) => {
        if (!window.confirm("Approve this bonus batch? (4-eye rule applies — initiator cannot approve.)")) return;
        setLoading(true);
        try {
            await api.post(`/compliance/bonus/batches/${batchId}/approve`);
            toast.success("Bonus batch approved");
            reload();
            if (detail && detail.id === batchId) openDetail(batchId);
        } catch (err) {
            toast.error(formatApiError(err, "Approval failed"));
        } finally {
            setLoading(false);
        }
    };

    const handleSeal = async (batchId) => {
        if (!window.confirm("Seal this batch? This is TERMINAL (cannot be undone).")) return;
        setLoading(true);
        try {
            await api.post(`/compliance/bonus/batches/${batchId}/seal`);
            toast.success("Bonus batch sealed");
            reload();
            if (detail && detail.id === batchId) openDetail(batchId);
        } catch (err) {
            toast.error(formatApiError(err, "Seal failed"));
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadFormC = async (batchId) => {
        setLoading(true);
        try {
            const r = await api.get(`/compliance/bonus/batches/${batchId}/form-c.csv`, {
                responseType: "blob",
            });
            const blob = new Blob([r.data], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `FormC_${batchId}.csv`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Form C downloaded");
        } catch (err) {
            toast.error(formatApiError(err, "Form C export failed"));
        } finally {
            setLoading(false);
        }
    };

    const openDetail = async (batchId) => {
        try {
            const [d, e] = await Promise.all([
                api.get(`/compliance/bonus/batches/${batchId}`),
                api.get(`/compliance/bonus/batches/${batchId}/employees`),
            ]);
            setDetail(d.data);
            setDetailEmployees(e.data.items || []);
            setDetailOpen(true);
        } catch (err) {
            toast.error(formatApiError(err, "Detail load failed"));
        }
    };

    return (
        <div className="p-6 space-y-4" data-testid="bonus-console">
            <PageHeader
                title="Statutory Bonus"
                subtitle="Payment of Bonus Act 1965 · FY-wise batches · Form C (Rule 4(c)) · D35-D snapshot architecture"
                actions={
                    <Button size="sm" variant="outline" onClick={reload} data-testid="bonus-refresh-btn">
                        <ArrowsClockwise size={14} className="mr-1" /> Refresh
                    </Button>
                }
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Builder */}
                <div className="border rounded p-4 bg-slate-50/30 space-y-3" data-testid="bonus-builder">
                    <h3 className="font-semibold text-sm">Initiate Bonus Batch</h3>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs">Financial Year</Label>
                            <Input
                                value={fy} onChange={(e) => setFy(e.target.value)}
                                placeholder="2025-2026" className="font-mono"
                                data-testid="bonus-fy-input"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Declared %</Label>
                            <Input
                                type="number" step="0.01" min="8.33" max="20"
                                value={declaredPct} onChange={(e) => setDeclaredPct(e.target.value)}
                                className="font-mono"
                                data-testid="bonus-pct-input"
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">Allocable Surplus (₹)</Label>
                        <Input
                            type="number" min="0"
                            value={allocSurplus} onChange={(e) => setAllocSurplus(e.target.value)}
                            className="font-mono"
                            data-testid="bonus-surplus-input"
                        />
                        <div className="text-[10px] text-slate-500 mt-1">Operator-input balance-sheet figure (D35-A — single field, no P&amp;L upload).</div>
                    </div>
                    <div>
                        <Label className="text-xs">FY Payroll Runs ({selectedRuns.length} selected)</Label>
                        <div className="border rounded max-h-60 overflow-y-auto bg-white" data-testid="bonus-runs-list">
                            {eligibleRuns.length === 0 ? (
                                <div className="text-xs text-slate-400 p-3 text-center">No eligible runs (need Closed/Approved/Paid)</div>
                            ) : eligibleRuns.map((r) => (
                                <label key={r.id} className="flex items-start gap-2 p-2 border-b text-xs hover:bg-slate-50 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={selectedRuns.includes(r.id)}
                                        onChange={() => toggleRun(r.id)}
                                        data-testid={`bonus-run-${r.id}`}
                                    />
                                    <div className="flex-1">
                                        <div className="font-mono">{r.id}</div>
                                        <div className="text-slate-500">{r.label || `${r.period_start} → ${r.period_end}`} · {r.status}</div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button size="sm" variant="outline" disabled={loading} onClick={handlePreview} data-testid="bonus-preview-btn">
                            Preview Eligibility
                        </Button>
                        {canGenerate && (
                            <Button size="sm" disabled={loading} onClick={handleCreate} data-testid="bonus-create-btn">
                                <Sparkle size={14} className="mr-1" /> Create Draft Batch
                            </Button>
                        )}
                    </div>
                </div>

                {/* Lifecycle explanation */}
                <div className="border rounded p-4 bg-slate-50/30 text-xs space-y-2">
                    <h3 className="font-semibold text-sm">Lifecycle — Draft → Approved → Sealed</h3>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><strong>Initiator</strong> ({canGenerate ? "you" : "bonus:generate role"}) creates a Draft batch. Per-employee rows are snapshotted IMMUTABLY at this point — never recomputed.</li>
                        <li><strong>Approver</strong> ({canApprove ? "you" : "bonus:approve role"}) signs off the Draft. <strong>D35-B 4-eye:</strong> initiator and approver MUST be different actors.</li>
                        <li><strong>Exporter</strong> ({canExport ? "you" : "bonus:export role"}) downloads Form C (Rule 4(c) Bonus Register).</li>
                        <li><strong>Sealer</strong> ({canSeal ? "you" : "compliance:seal role"}) terminally seals the batch (cannot be undone).</li>
                    </ul>
                    <div className="bg-amber-50 border border-amber-200 rounded p-2 mt-2 text-amber-900">
                        <strong>Eligibility (Payment of Bonus Act 1965, §8):</strong> ≤ ₹21,000/m basic+DA · ≥ 30 days worked in FY. Bonus capped at ₹7,000/m basic+DA × declared% × months worked.
                    </div>
                </div>
            </div>

            {/* Batch history */}
            <div className="border rounded">
                <div className="p-2 bg-slate-50 border-b font-semibold text-sm flex justify-between items-center">
                    <span>Bonus Batches · {batches.length}</span>
                </div>
                <table className="w-full text-xs" data-testid="bonus-batches-table">
                    <thead className="bg-slate-50 border-b uppercase text-slate-600">
                        <tr>
                            <th className="text-left px-2 py-1">Batch ID</th>
                            <th className="text-left px-2 py-1">FY</th>
                            <th className="text-right px-2 py-1">%</th>
                            <th className="text-right px-2 py-1">Eligible / Total</th>
                            <th className="text-right px-2 py-1">Total Bonus</th>
                            <th className="text-left px-2 py-1">Status</th>
                            <th className="text-left px-2 py-1">Initiated</th>
                            <th className="text-left px-2 py-1">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {batches.length === 0 && (
                            <tr><td colSpan="8" className="text-center text-slate-400 p-6">No bonus batches yet</td></tr>
                        )}
                        {batches.map((b) => (
                            <tr key={b.id} className="border-b hover:bg-slate-50/50 cursor-pointer" data-testid={`bonus-row-${b.id}`} onClick={() => openDetail(b.id)}>
                                <td className="px-2 py-1 font-mono">{b.id}</td>
                                <td className="px-2 py-1">{b.financial_year}</td>
                                <td className="px-2 py-1 text-right font-mono">{b.declared_percent}%</td>
                                <td className="px-2 py-1 text-right font-mono">{b.n_eligible} / {b.n_total_employees}</td>
                                <td className="px-2 py-1 text-right font-mono">{fmtMoney(b.total_bonus)}</td>
                                <td className="px-2 py-1">
                                    <Badge className={STATUS_COLORS[b.status] || ""}>{b.status}</Badge>
                                </td>
                                <td className="px-2 py-1 text-[10px] text-slate-500">{b.initiated_by_email || "—"}<br />{b.initiated_at?.slice(0, 16).replace("T", " ")}</td>
                                <td className="px-2 py-1" onClick={(e) => e.stopPropagation()}>
                                    {b.status === "Draft" && canApprove && (
                                        <Button size="sm" variant="outline" onClick={() => handleApprove(b.id)} data-testid={`bonus-approve-${b.id}`}>
                                            <CheckCircle size={12} className="mr-1" /> Approve
                                        </Button>
                                    )}
                                    {b.status === "Approved" && canExport && (
                                        <Button size="sm" variant="outline" onClick={() => handleDownloadFormC(b.id)} data-testid={`bonus-formc-${b.id}`}>
                                            <DownloadSimple size={12} className="mr-1" /> Form C
                                        </Button>
                                    )}
                                    {b.status === "Approved" && canSeal && (
                                        <Button size="sm" variant="outline" onClick={() => handleSeal(b.id)} data-testid={`bonus-seal-${b.id}`} className="ml-1">
                                            <ShieldCheck size={12} className="mr-1" /> Seal
                                        </Button>
                                    )}
                                    {b.status === "Sealed" && (
                                        <Badge className="bg-blue-50 text-blue-700"><Lock size={10} className="mr-1 inline" /> Sealed</Badge>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Preview dialog (read-only eligibility) */}
            <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Bonus Eligibility Preview · FY {preview?.financial_year}</DialogTitle>
                        <DialogDescription className="text-xs">
                            Read-only preview. No batch is persisted until you click Create Draft Batch.
                        </DialogDescription>
                    </DialogHeader>
                    {preview && (
                        <div className="text-xs space-y-3">
                            <div className="grid grid-cols-4 gap-3">
                                <div className="border rounded p-2 bg-slate-50">
                                    <div className="text-[9px] uppercase text-slate-500">Eligible</div>
                                    <div className="font-mono font-bold text-emerald-700">{preview.n_eligible}</div>
                                </div>
                                <div className="border rounded p-2 bg-slate-50">
                                    <div className="text-[9px] uppercase text-slate-500">Excluded</div>
                                    <div className="font-mono font-bold text-rose-700">{preview.n_excluded}</div>
                                </div>
                                <div className="border rounded p-2 bg-slate-50">
                                    <div className="text-[9px] uppercase text-slate-500">Total Bonus</div>
                                    <div className="font-mono font-bold">{fmtMoney(preview.totals?.total_bonus)}</div>
                                </div>
                                <div className="border rounded p-2 bg-slate-50">
                                    <div className="text-[9px] uppercase text-slate-500">Avg Allocation</div>
                                    <div className="font-mono">{preview.n_eligible ? fmtMoney(preview.totals.total_bonus / preview.n_eligible) : "—"}</div>
                                </div>
                            </div>
                            <table className="w-full text-[10px] border">
                                <thead className="bg-slate-100">
                                    <tr>
                                        <th className="text-left px-1 py-0.5">Code</th>
                                        <th className="text-left px-1 py-0.5">Name</th>
                                        <th className="text-right px-1 py-0.5">Avg B+DA</th>
                                        <th className="text-right px-1 py-0.5">Capped</th>
                                        <th className="text-right px-1 py-0.5">Months</th>
                                        <th className="text-right px-1 py-0.5">Bonus</th>
                                        <th className="text-left px-1 py-0.5">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(preview.employees || []).slice(0, 200).map((l) => (
                                        <tr key={l.employee_id} className="border-b">
                                            <td className="px-1 py-0.5 font-mono">{l.employee_code}</td>
                                            <td className="px-1 py-0.5">{l.employee_name}</td>
                                            <td className="px-1 py-0.5 text-right font-mono">{fmtMoney(l.avg_basic_da_monthly)}</td>
                                            <td className="px-1 py-0.5 text-right font-mono">{fmtMoney(l.capped_basic_da_monthly)}</td>
                                            <td className="px-1 py-0.5 text-right font-mono">{l.months_worked}</td>
                                            <td className="px-1 py-0.5 text-right font-mono">{fmtMoney(l.bonus_amount)}</td>
                                            <td className="px-1 py-0.5 text-[9px]">
                                                {l.eligible ? <span className="text-emerald-700">Eligible</span> :
                                                    <span className="text-rose-700" title={l.excluded_reason}>Excluded</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPreviewOpen(false)}>Close</Button>
                        {canGenerate && (
                            <Button onClick={handleCreate} data-testid="bonus-create-from-preview-btn">
                                <Sparkle size={14} className="mr-1" /> Create Draft Batch
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Detail dialog */}
            <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
                <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{detail?.id} · FY {detail?.financial_year} · {detail?.declared_percent}%</DialogTitle>
                        <DialogDescription className="text-xs">
                            Immutable snapshot of {detail?.n_total_employees || 0} employees · {detail?.n_eligible || 0} eligible · {fmtMoney(detail?.total_bonus)} total
                        </DialogDescription>
                    </DialogHeader>
                    {detail && (
                        <div className="text-xs space-y-2">
                            <div className="grid grid-cols-3 gap-2 text-[10px]">
                                <div className="border rounded p-2">
                                    <div className="uppercase text-slate-500">Initiator</div>
                                    <div className="font-mono">{detail.initiated_by_email}</div>
                                    <div className="text-slate-400">{detail.initiated_at?.slice(0, 16).replace("T", " ")}</div>
                                </div>
                                <div className="border rounded p-2">
                                    <div className="uppercase text-slate-500">Approver</div>
                                    <div className="font-mono">{detail.approved_by_email || "—"}</div>
                                    <div className="text-slate-400">{detail.approved_at?.slice(0, 16).replace("T", " ") || "Pending"}</div>
                                </div>
                                <div className="border rounded p-2">
                                    <div className="uppercase text-slate-500">Sealer</div>
                                    <div className="font-mono">{detail.sealed_by_email || "—"}</div>
                                    <div className="text-slate-400">{detail.sealed_at?.slice(0, 16).replace("T", " ") || "Pending"}</div>
                                </div>
                            </div>
                            <table className="w-full text-[10px] border">
                                <thead className="bg-slate-100">
                                    <tr>
                                        <th className="text-left px-1 py-0.5">Code</th>
                                        <th className="text-left px-1 py-0.5">Name</th>
                                        <th className="text-left px-1 py-0.5">PAN</th>
                                        <th className="text-right px-1 py-0.5">Avg B+DA</th>
                                        <th className="text-right px-1 py-0.5">Capped</th>
                                        <th className="text-right px-1 py-0.5">Days</th>
                                        <th className="text-right px-1 py-0.5">Months</th>
                                        <th className="text-right px-1 py-0.5">Bonus</th>
                                        <th className="text-left px-1 py-0.5">Eligible?</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {detailEmployees.map((l) => (
                                        <tr key={l.id} className="border-b">
                                            <td className="px-1 py-0.5 font-mono">{l.employee_code}</td>
                                            <td className="px-1 py-0.5">{l.employee_name}</td>
                                            <td className="px-1 py-0.5 font-mono">{l.pan || "—"}</td>
                                            <td className="px-1 py-0.5 text-right font-mono">{fmtMoney(l.avg_basic_da_monthly)}</td>
                                            <td className="px-1 py-0.5 text-right font-mono">{fmtMoney(l.capped_basic_da_monthly)}</td>
                                            <td className="px-1 py-0.5 text-right font-mono">{l.days_worked}</td>
                                            <td className="px-1 py-0.5 text-right font-mono">{l.months_worked}</td>
                                            <td className="px-1 py-0.5 text-right font-mono">{fmtMoney(l.bonus_amount)}</td>
                                            <td className="px-1 py-0.5">{l.eligible ? "Y" : <span className="text-rose-600" title={l.excluded_reason}>N</span>}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDetailOpen(false)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
